import React, { useCallback, useEffect, useState } from 'react';
import ClinicOrderForms from '../components/ClinicOrderForms';
import PageHeader from '../components/PageHeader';
import ClinicHubDialog from '../components/ClinicHubDialog';
import clinicService from '../services/clinicService';
import hubService from '../services/hubService';
import { HEADER_CELL } from '../utils/tableColumns';
import { useT } from '../i18n/LanguageContext';
import { nextPracticeRuling, practiceLabel } from '../features/clinics/practiceStatus';
import {
  ClinicUserRoles,
  CreditLimitCell,
  NewClinicUser,
  RateCell,
  RepAssignments,
  unwrap,
} from '../features/clinics/ClinicAdminPanels';

export { CreditLimitCell, RateCell } from '../features/clinics/ClinicAdminPanels';

// Practice status is tri-state because unreviewed QuickBooks customers are distinct from clinics
// an administrator has explicitly confirmed or hidden.
export default function Clinics() {
  const [showOrderForms, setShowOrderForms] = useState(false);
  const t = useT();
  const [clinics, setClinics] = useState([]);
  /** Rows an admin took out of the list. Held separately so nothing else on this page can see them. */
  const [removed, setRemoved] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState('');
  const [usersRefresh, setUsersRefresh] = useState(0);

  /**
   * Load live and removed lists in parallel. The removed list is not lazily loaded, because its count makes
   * the removal honest: a row you cannot see a way back to is not "restorable", it is gone, and
   * this database has no backups. The count sits on the toggle, so it is visible before you need it.
   *
   * Kept as two arrays rather than one filtered list because everything else on this page - the rep
   * assignment picker, the clinic-user form - must only ever see LIVE clinics.
   */
  const fetchClinics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [live, gone] = await Promise.all([
        clinicService.adminList(),
        clinicService.adminList({ removed: true }),
      ]);
      setClinics(unwrap(live));
      setRemoved(unwrap(gone));
    } catch (err) {
      setError('Could not load clinics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove a row from the list. Confirmed first: it is reversible, but it also pulls the clinic out
   * of every order and IVR picker, so it is not a click to make by accident.
   */
  const handleRemove = useCallback(async (clinic) => {
    if (
      !window.confirm(
        t('Remove {name} from the clinics list?', { name: clinic.name }) +
          '\n\n' +
          t('It will also disappear from every order and IVR picker. QuickBooks is NOT changed, existing orders keep their clinic, and you can restore it from "Removed".'),
      )
    ) {
      return;
    }
    try {
      await clinicService.remove(clinic._id);
      setClinics((prev) => prev.filter((c) => c._id !== clinic._id));
      setRemoved((prev) => [...prev, clinic].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (err) {
      alert(t('Failed to remove'));
    }
  }, [t]);

  const handleRestore = useCallback(async (clinic) => {
    try {
      await clinicService.restore(clinic._id);
      setRemoved((prev) => prev.filter((c) => c._id !== clinic._id));
      setClinics((prev) => [...prev, clinic].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (err) {
      alert(t('Failed to restore'));
    }
  }, [t]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  const handleRateSaved = useCallback((clinicId, ratePerUnit) => {
    setClinics((prev) => prev.map((c) => (c._id === clinicId ? { ...c, ratePerUnit } : c)));
  }, []);

  const handleCreditLimitSaved = useCallback((clinicId, creditLimit) => {
    setClinics((prev) => prev.map((c) => (c._id === clinicId ? { ...c, creditLimit } : c)));
  }, []);

  /**
   * MY HUB FROM A CLINIC ROW (9/4/26). The admin list returns the raw clinic documents; the hub
   * dialog wants the hub row (allowed products populated, ledger, logins, canManage), which only
   * GET /hub/clinics builds - so the row is fetched on the click, and the dialog opens on it with
   * every control live. A clinic the hub cannot list is one that is hidden or unconfirmed, and
   * the fix for that is on this very page.
   */
  const [hub, setHub] = useState(null); // { clinic, products } while the dialog is open
  const openHub = useCallback(async (clinic) => {
    try {
      const res = await hubService.list();
      const body = res.data?.data || {};
      const rows = Array.isArray(body.data) ? body.data : [];
      const row = rows.find((r) => r._id === clinic._id);
      if (!row) {
        alert(t('This clinic is not listable in My Hub - confirm it as a practice first.'));
        return;
      }
      setHub({ clinic: row, products: Array.isArray(body.products) ? body.products : [] });
    } catch (err) {
      alert(t('Could not load My Hub for this clinic.'));
    }
  }, [t]);

  // The dialog hands back the hub row it saved; the badges on this page read the raw document's
  // fields, so translate the populated allow-list back into ids.
  const handleHubSaved = useCallback((row) => {
    if (!row || !row._id) return;
    setHub((prev) => (prev && prev.clinic._id === row._id ? { ...prev, clinic: { ...prev.clinic, ...row } } : prev));
    setClinics((prev) => prev.map((c) => {
      if (c._id !== row._id) return c;
      const next = { ...c };
      if ('orderingEnabled' in row) next.orderingEnabled = row.orderingEnabled;
      if ('allowedProductIds' in row) {
        next.allowedProductIds = row.allowedProductIds;
      } else if ('allowedProducts' in row) {
        next.allowedProductIds = row.allowedProducts === null
          ? null
          : (row.allowedProducts || []).map((p) => (p && p._id) || p);
      }
      if ('creditLimit' in row) next.creditLimit = row.creditLimit;
      return next;
    }));
  }, []);


  const handlePracticeToggle = useCallback(async (clinic) => {
    const next = nextPracticeRuling(clinic.isPractice);
    try {
      await clinicService.setPractice(clinic._id, next);
      setClinics((prev) => prev.map((c) => (c._id === clinic._id ? { ...c, isPractice: next } : c)));
    } catch (err) {
      alert(t('Failed to update'));
    }
  }, [t]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncSummary('');
    try {
      const res = await clinicService.syncFromQuickBooks();
      const s = res.data?.data || {};
      setSyncSummary(
        t('Synced from QuickBooks: {fetched} customers fetched, {created} new, {updated} updated.', {
          fetched: s.fetched ?? 0,
          created: s.created ?? 0,
          updated: s.updated ?? 0,
        }),
      );
      await fetchClinics();
    } catch (err) {
      alert(t('Sync failed - is QuickBooks configured?'));
    } finally {
      setSyncing(false);
    }
  };

  if (showOrderForms) return <ClinicOrderForms onBack={() => setShowOrderForms(false)} />;

  const rows = showRemoved ? removed : clinics;

  return (
    <div>
      <PageHeader
        title={showRemoved ? t('Removed clinics') : t('Clinics')}
        subtitle={
          showRemoved
            ? t('Taken out of the list and every picker. Still in QuickBooks, and existing orders keep their clinic.')
            : t('Clinics are synced from QuickBooks Customers - QuickBooks is the system of record.')
        }
      >
        <button
          onClick={() => setShowOrderForms(true)}
          className="h-[46px] px-4 bg-white border border-[#e2e8f0] text-[#0f172a] text-sm font-semibold rounded-[10px] hover:bg-gray-50"
        >{t('Order forms & addresses')}</button>
        <button
          onClick={() => setShowRemoved((v) => !v)}
          className="h-[46px] px-4 bg-white border border-[#e2e8f0] text-[#0f172a] text-sm font-semibold rounded-[10px] hover:bg-gray-50 transition-colors flex items-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[18px]">
            {showRemoved ? 'arrow_back' : 'visibility_off'}
          </span>
          {showRemoved ? t('Back to clinics') : t('Removed ({count})', { count: removed.length })}
        </button>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="h-[46px] px-4 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[18px]">sync</span>
          {syncing ? t('Syncing...') : t('Sync from QuickBooks')}
        </button>
      </PageHeader>

      {syncSummary && (
        <div role="status" aria-live="polite" className="mb-4 px-4 py-3 rounded-[10px] bg-[rgba(222,252,237,0.6)] text-[13px] text-[#007a55]">
          {syncSummary}
        </div>
      )}
      {error && <div role="alert" className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">{t(error)}</div>}

      {!showRemoved && (
        <>
          <RepAssignments clinics={clinics.filter((c) => c.isActive !== false && c.isPractice !== false)} onSaved={fetchClinics} />
          <NewClinicUser clinics={clinics.filter((c) => c.isActive !== false && c.isPractice !== false)} onCreated={() => setUsersRefresh((n) => n + 1)} />
          <ClinicUserRoles refreshKey={usersRefresh} />
        </>
      )}

      {loading ? (
        <div role="status" aria-live="polite" className="flex items-center justify-center h-40">
          <span className="sr-only">{t('Loading clinics...')}</span>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
        </div>
      ) : (
        <div data-testid="clinic-table" role="table" aria-label={showRemoved ? t('Removed clinics') : t('Clinics')} className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
          <div role="rowgroup" className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]">
            <div role="row" className="grid grid-cols-[minmax(116px,3.12fr)_minmax(100px,2.38fr)_minmax(92px,2.31fr)_minmax(56px,1.96fr)_minmax(60px,1.98fr)_minmax(84px,2.21fr)_minmax(96px,2.3fr)_minmax(80px,2.2fr)_88px] px-5 py-4">
              {['NAME', 'COMPANY', 'PHONE', 'STATUS', 'PRACTICE', 'RATE ($/UNIT)', 'CREDIT LIMIT', 'LAST SYNCED', ''].map((col) => (
                <span
                  role="columnheader"
                  key={col || 'actions'}
                  title={col === 'PRACTICE' ? t('Is this QuickBooks customer a real medical practice? "Hidden" removes it from every order/IVR picker. Synced bank rows, suppliers, and other non-practices belong Hidden.') : ''}
                  className={HEADER_CELL}
                >
                  {t(col)}
                </span>
              ))}
            </div>
          </div>
          <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
            {rows.length === 0 ? (
              <div role="row" className="px-5 py-8 text-center text-sm text-[#64748b]"><span role="cell">
                {showRemoved
                  ? t('Nothing has been removed from the list.')
                  : t('No clinics yet. Click "Sync from QuickBooks" to pull the customer list.')}
              </span></div>
            ) : (
              rows.map((c) => {
                // My Hub badges: what the rep has set on this clinic. The admin list returns the
                // raw document, so the allow-list is ids (null / absent = every product).
                const orderingOff = c.orderingEnabled === false;
                const limitedTo = Array.isArray(c.allowedProductIds) && c.allowedProductIds.length > 0
                  ? c.allowedProductIds.length
                  : 0;
                return (
                <div key={c._id} role="row" className="grid grid-cols-[minmax(116px,3.12fr)_minmax(100px,2.38fr)_minmax(92px,2.31fr)_minmax(56px,1.96fr)_minmax(60px,1.98fr)_minmax(84px,2.21fr)_minmax(96px,2.3fr)_minmax(80px,2.2fr)_88px] px-5 py-4 items-center hover:bg-gray-50/50 transition-colors">
                  <div role="cell" className="min-w-0 pr-2">
                    <span className="text-xs font-medium text-[#0f172a]">{c.name}</span>
                    {(orderingOff || limitedTo > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {orderingOff && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#ffebec] text-[#f23e41]">
                            {t('Ordering off')}
                          </span>
                        )}
                        {limitedTo > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#f3e8ff] text-[#7e22ce]">
                            {limitedTo === 1 ? t('{n} product', { n: limitedTo }) : t('{n} products', { n: limitedTo })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span role="cell" className="text-xs font-medium text-[#64748b]">{c.companyName || '—'}</span>
                  <span role="cell" className="text-xs font-medium text-[#64748b]">{c.phone || '—'}</span>
                  <span role="cell" className={`inline-flex items-center justify-center w-fit px-2.5 py-0.5 rounded-[6px] text-xs font-medium ${
                    c.isActive !== false ? 'bg-[rgba(222,252,237,0.6)] text-[#007a55]' : 'bg-[rgba(254,226,226,0.6)] text-[#f23e41]'
                  }`}>
                    {c.isActive !== false ? t('Active') : t('Inactive')}
                  </span>
                  {/* Unreviewed is shown as its own state rather than borrowing the look of a
                      confirmed practice - that is what made a whole company file of unruled
                      records read as though someone had already been through them. */}
                  <span role="cell"><button
                    onClick={() => handlePracticeToggle(c)}
                    title={
                      c.isPractice === true
                        ? t('Confirmed as a real practice - click to hide it from every order/IVR picker')
                        : c.isPractice === false
                          ? t('Hidden from all pickers - click to confirm it as a real practice')
                          : t('Nobody has ruled on this record yet - click to confirm it as a real practice')
                    }
                    className={`inline-flex items-center justify-center w-fit px-2.5 py-0.5 rounded-[6px] text-xs font-medium transition-colors ${
                      c.isPractice === true
                        ? 'bg-[#e6f1ff] text-[#0069c4] hover:bg-[#d4e8ff]'
                        : c.isPractice === false
                          ? 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'
                          : 'bg-[#fff8db] text-[#8a6100] hover:bg-[#ffefb0]'
                    }`}
                  >
                    {t(practiceLabel(c.isPractice))}
                  </button></span>
                  <span role="cell"><RateCell clinic={c} onRateSaved={handleRateSaved} /></span>
                  <span role="cell"><CreditLimitCell clinic={c} onSaved={handleCreditLimitSaved} /></span>
                  <span role="cell" className="text-xs font-medium text-[#64748b]">
                    {c.qbSyncedAt ? new Date(c.qbSyncedAt).toLocaleString('en-US') : '—'}
                  </span>
                  {showRemoved ? (
                    <span role="cell"><button
                      onClick={() => handleRestore(c)}
                      title={t('Put this clinic back in the list and the pickers')}
                      className="justify-self-end text-xs font-semibold text-[#0069c4] hover:underline"
                    >
                      {t('Restore')}
                    </button></span>
                  ) : (
                    <span role="cell" className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openHub(c)}
                        title={t('Open in My Hub')}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] text-[#94a3b8] hover:text-[#0069c4] hover:bg-[#e6f1ff] transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">hub</span>
                      </button>
                      <button
                        onClick={() => handleRemove(c)}
                        title={t('Remove from this list and every picker. QuickBooks is not changed and existing orders keep their clinic.')}
                        className="text-[#94a3b8] hover:text-[#f23e41] transition-colors"
                        aria-label={t('Remove {name}', { name: c.name })}
                      >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                      </button>
                    </span>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <ClinicHubDialog
        open={!!hub}
        clinic={hub?.clinic || null}
        products={hub?.products || []}
        canManage
        onClose={() => setHub(null)}
        onSaved={handleHubSaved}
      />
    </div>
  );
}
